import React, { useCallback, useEffect, useMemo, useState } from 'react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import flatMap from 'lodash/flatMap';
import pick from 'lodash/pick';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Spinner, useToast } from '@chakra-ui/react';

import { getBaBSingleProducts, getBundleDiscounts } from '../../bundles/api';
import { getIdFromGid } from '../utils';
import { useBundleSetting } from '../state/BundleSettingContext';
import {
  getCurrencyFormatter,
  getGidFromIdForResource,
  calculateDiscount,
  isGlobalProductList,
  splitProductInVariants,
  notEmpty,
} from '../../utils';

import {
  extractVariantAndImage,
  getOnlySelectedVariants,
  getDiscount,
  getPricingPolicyCycleDiscount,
  formatLiquidProductToGlobalProduct,
  getAddonDiscount,
} from '../../utils/BundleUtils';
import { useCreateBundle } from '../../bundles/CreateBundle/useCreateBundle';

import Button from '../../components/Button';
import { Span } from '../../components/Typography';
import { ProductsHandler } from '../../components/Bundle/CreateBundleContent';
import { BundleSelector } from '../../components/Bundle/BundleSizeSelector';
import SlidePanel from '../../components/SlidePanel';
import ProgressBar from '../../bundles/CreateBundle/ProgressBar';
import { useControlSlidePanel } from '../../components/SlidePanel/useControlSlidePanel';
import { VariantStyle } from '../../../components/BundleSettings/types';
import {
  CreateBundleContainer,
  CreateBundleFooter,
  CreateBundleButtonInfo,
} from '../../components/Bundle/BundleBoxStructure';
import { filterProducts } from '../../bundles/bundleUtils';
import { FiltersSection } from '../../bundles/CreateBundle/FiltersSection';
import { useGlobalContext } from '../state/GlobalContext';
import { useQuery } from 'react-query';
import { getBundleByProductId } from '../api';
import { useBundleSectionsSelection } from '../../bundles/BabSections/useBabSections';
import { CreateBundleWithSections } from '../../bundles/BabSections/CreateBundleSections';
import {
  checkSectionsLimits,
  findSectionSettings,
} from '../../bundles/BabSections/utils';

const MAX_CHUNK_SIZE = 19;
const BODY_BAB_CLASS = 'awt-bab-open';

const SpinnerContainer = styled.div.attrs({
  className: 'bundle-loading-container',
})`
  position: relative;
  padding: 100px 0;
`;

export interface BundleCustomizeProps {
  product?: Product;
  subscription: Subscription;
  shopConfiguration?: ShopConfiguration;
  bundleProduct: GlobalProduct;
  sellingPlans: SellingPlan[];
  addonDiscount?: AddonsDiscounts;
  onConfirm: (
    newBundleProps,
    lineId?: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => void;
  onClose: () => void;
}

const BundleCustomize: React.FC<BundleCustomizeProps> = ({
  bundleProduct,
  product,
  subscription,
  shopConfiguration,
  sellingPlans,
  addonDiscount,
  onConfirm,
  onClose,
}) => {
  const { authToken } = useGlobalContext();
  const bundleProductId = bundleProduct.id;
  const sellingPlan = getIdFromGid(sellingPlans?.[0]?.gId as string);
  const variant = bundleProduct?.variants?.[0]?.id;

  const pricingPolicy = getPricingPolicyCycleDiscount(
    product?.PricingPolicyCycleDiscounts,
    product?.SuccessfulBillingAttempts,
  );

  const discount =
    getDiscount(pricingPolicy) || getAddonDiscount(addonDiscount);

  const { data: bundleData, isLoading } = useQuery(
    ['bundle', bundleProductId],
    async () => {
      const bundle = await getBundleByProductId({
        productId: `${bundleProductId}`,
        authToken,
      });

      const bundleFormatted = await formatBundleCollections(
        bundle,
        sellingPlan as string,
        variant as string,
        {},
      );

      return bundleFormatted;
    },
  );

  React.useEffect(() => {
    document.body.classList.toggle(BODY_BAB_CLASS, true);

    return () => {
      document.body.classList.toggle(BODY_BAB_CLASS, false);
    };
  }, []);

  if (isLoading || !bundleData) {
    return (
      <CreateBundleContainer>
        <SpinnerContainer className="bundle-loading-container">
          <Spinner size="xl" style={{ margin: '0 auto', display: 'block' }} />
        </SpinnerContainer>
      </CreateBundleContainer>
    );
  }

  return (
    <BundleCustomizeContent
      subscription={subscription}
      variantId={bundleProduct.variants?.[0]?.id}
      subscriptionId={product.PK}
      bundleData={bundleData}
      closeModal={onClose}
      productsInBundle={{}}
      updateBundle={onConfirm}
      lineId={product.SubscriptionContractLineId}
      oneTimeAddon={product.OneTimeAddon}
      currencyCode={product.PricingPolicyBasePriceCurrencyCode}
      discount={discount}
      sellingPlans={sellingPlans}
      shopConfiguration={shopConfiguration}
      product={product}
    />
  );
};

export default BundleCustomize;

export type Discount = Partial<{
  DynamicDiscountAmount: string | number;
  DynamicDiscountCode: string;
  DynamicDiscountId: string;
  DynamicDiscountType: string;
}>;

interface BundleCustomizeContentProps {
  variantId: string | number;
  subscriptionId: string;
  bundleData: LiquidBundleParsed;
  updateBundle: (
    newBundleProps,
    lineId: string,
    dynamicBabItems?: unknown,
    sellingPlan?: string | number,
    discountCodes?: string[],
    oneTimeAddon?: boolean,
    addonGroupId?: string,
  ) => void;
  lineId: string;
  oneTimeAddon?: boolean;
  closeModal: () => void;
  productsInBundle: Record<string, number>;
  currencyCode?: string;
  sellingPlans: SellingPlan[];
  discount?: {
    type: string;
    value: number;
  };
  subscription: any;
  shopConfiguration?: ShopConfiguration;
  product?: Product;
}

const BundleCustomizeContent = ({
  bundleData,
  lineId,
  oneTimeAddon,
  closeModal,
  productsInBundle,
  updateBundle,
  variantId,
  currencyCode,
  discount,
  subscription,
  sellingPlans,
  shopConfiguration,
  product,
}: BundleCustomizeContentProps) => {
  const toast = useToast();
  const { t } = useTranslation();
  const { bundle } = bundleData;
  const isDynamic = bundle.Type === 'dynamicBaB';
  const isPrepaid = useMemo(() => subscription.HasPrepaidLines, [subscription]);
  const hasBabSections =
    bundle?.Sections &&
    isArray(bundle?.Sections) &&
    bundle?.Sections?.length >= 1;

  const {
    basePrice,
    productsLength,
    size,
    isSaveable,
    canAddProducts,
    hasDynamicSize,
    addProduct,
    removeProduct,
    selectedProducts,
    getItemProperties,
    getSelectedProductsWithPropsForCart,
    updateBoxSize,
    sellingPlan,
    initialSelectedProducts,
    variantId: selectedSetting,
    dynamicVariantId,
    setSelectedProducts,
    discountedPrice,
    settings,
  } = useCreateBundle({
    bundle,
    product: bundleData.productBundle as any,
    selectedDynamicVariantId: subscription.Lines.find(
      ({ SubscriptionContractLineId }) => SubscriptionContractLineId === lineId,
    )?.CustomAttributes?.find(({ key }) => key === '_variantId')?.value,
    productsInCollection: bundleData.productsInCollection,
    productsInAddonsCollection: bundleData.productsInAddonsCollection,
    initialSelectedProducts: productsInBundle,
    subscription,
    sellingPlans,
    shopConfiguration,
  });

  const {
    customerPortalSettings,
    hideBaBSoldOutOptions,
    ignoreBaBOutOfStockAllowance,
  } = useGlobalContext();
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);

  const getAllowedBaBSwapVariants = useCallback(() => {
    const allowedBaBSwapVariants = subscription.Lines.find(
      ({ SubscriptionContractLineId }) => SubscriptionContractLineId === lineId,
    )?.allowedBaBSwapVariants;
    if (customerPortalSettings?.productChange === 'SAME_SUBSCRIPTION') {
      const newSettings = bundle.Settings.filter(({ VariantId }) =>
        allowedBaBSwapVariants
          ? allowedBaBSwapVariants?.includes(VariantId)
          : true,
      );

      return newSettings;
    }

    return bundle.Settings;
  }, [
    bundle.Settings,
    customerPortalSettings?.productChange,
    lineId,
    subscription.Lines,
  ]);

  const products: GlobalVariant[] | GlobalProduct[] = useMemo(() => {
    return bundleData.productsInCollection?.length
      ? bundleData.productsInCollection
      : (bundleData.productsInSections as any).flatMap(
          ({ products, productsInCollections }) => [
            ...products.flat().map(({ productData = {} }) => productData),
            ...productsInCollections
              .flat()
              .map(({ productData = {} }) => productData),
          ],
        );
  }, [bundleData.productsInCollection, bundleData.productsInSections]);

  const [filteredProducts, setFilteredProducts] = useState<
    GlobalVariant[] | GlobalProduct[]
  >(products);

  useEffect(() => {
    const bundleId = getIdFromGid(bundleData?.bundle?.ProductId);
    if (bundleId) {
      const filtersBasedOnSettings =
        subscription.CustomAttributes?.find(
          ({ key }) => key === `_bundle_filters_${bundleId}`,
        )?.value?.split(',') || [];

      setSelectedFilters(filtersBasedOnSettings);
    }
  }, [bundleData?.bundle?.ProductId, subscription?.CustomAttributes]);

  useEffect(() => {
    const allFilters =
      bundle?.BuildABoxFilterGroups?.flatMap(({ Filters, Id }) =>
        Filters.map((filter) => ({ ...filter, groupId: Id })),
      ) || [];

    const selectedFiltersConfig = allFilters?.filter(({ Id }) =>
      selectedFilters.includes(Id),
    );

    const products = bundleData.productsInCollection?.length
      ? bundleData.productsInCollection
      : (bundleData.productsInSections as any).flatMap(
          ({ products, productsInCollections }) => [
            ...products.flat().map(({ productData = {} }) => productData),
            ...productsInCollections
              .flat()
              .map(({ productData = {} }) => productData),
          ],
        );

    const newProducts = filterProducts({
      selectedFilters: selectedFiltersConfig,
      products: products,
      variantStyle: bundle.VariantStyle,
    });

    setFilteredProducts(
      sortProductsBySelected(newProducts, initialSelectedProducts),
    );
  }, [
    bundle?.BuildABoxFilterGroups,
    bundle.VariantStyle,
    bundleData.productsInCollection,
    bundleData.productsInSections,
    initialSelectedProducts,
    selectedFilters,
  ]);

  const handleResetFilters = (groupFilters) => {
    const groupFiltersIds = groupFilters.map(({ Id }) => Id);
    const newFilters = selectedFilters.filter(
      (selectedFilterId) => !groupFiltersIds.includes(selectedFilterId),
    );
    setSelectedFilters(newFilters);
  };

  const currency =
    currencyCode || get(window, ['Shopify', 'currency', 'active'], 'USD');
  const format = getCurrencyFormatter(currency);

  const { isOpen, handleOnCloseSlidePanel, productInfo, handleOnCardClick } =
    useControlSlidePanel();

  const { updateBundleSetting, bundleSetting } = useBundleSetting();
  const bundleSettingSelected = useMemo(
    () =>
      bundle.Settings?.find(({ VariantId }) => VariantId === selectedSetting) ||
      bundle.Settings?.[0],
    [bundle.Settings, selectedSetting],
  );

  useEffect(() => {
    if (bundleSettingSelected) {
      updateBundleSetting(bundleSettingSelected);
    }
  }, [bundleSettingSelected]);

  useEffect(() => {
    const shopDomain = bundle.PK?.replace(/SHOP#/, '');
    const getBundleSetting = async () => {
      const setting = await getBundleDiscounts({
        bundleId: getIdFromGid(bundle.ProductId),
        variantId: variantId,
        shopDomain,
      });

      return setting;
    };

    getBundleSetting().then((setting) => {
      updateBundleSetting(setting);
    });
  }, [bundle.PK, variantId, bundle.ProductId, updateBundleSetting]);

  const dynamicBundleDiscount =
    isDynamic && bundleSetting
      ? pick(bundleSetting, [
          'DynamicDiscountAmount',
          'DynamicDiscountCode',
          'DynamicDiscountId',
          'DynamicDiscountType',
        ])
      : {};

  const handleNext = () => {
    if (hasBabSections) {
      const sectionsWithError = checkSectionsLimits(
        bundleData.productsInSections,
        selectedProductsBySection,
        size,
      );
      if (sectionsWithError.length) {
        sectionsWithError.forEach((sectionWithError) => {
          const { ErrorLabel = '' } = findSectionSettings(
            sectionWithError.settings,
            size,
          );
          const errMessage = `${sectionWithError.name} - ${ErrorLabel}`;
          toast({
            title: errMessage,
            description: '',
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        });
        return;
      }
    }
    const itemProperties = getItemProperties();
    let customAttributes = convertPropertiesToAttributes(itemProperties);
    if (isDynamic) {
      customAttributes = [
        ...customAttributes,
        { key: '_bundleType', value: 'dynamicBaB' },
      ];
    }
    const newLineProps = {
      customAttributes,
      ...(dynamicVariantId !== variantId && isDynamic
        ? { productVariantId: dynamicVariantId }
        : { productVariantId: bundleSettingSelected?.VariantId }),
    };

    const sellingPlanGid = sellingPlan
      ? getGidFromIdForResource(`${sellingPlan}`, 'SellingPlan')
      : undefined;

    updateBundle(
      newLineProps,
      lineId,
      getSelectedProductsWithPropsForCart(),
      sellingPlanGid,
      [dynamicBundleDiscount.DynamicDiscountCode].filter(Boolean),
      oneTimeAddon,
      product?.AddonGroupId,
    );

    closeModal();
  };

  const handleOnUpdateSetting = useCallback(
    (variantId: string) => {
      updateBoxSize(variantId);
    },
    [updateBoxSize],
  );

  const calculatedDiscount = calculateDiscount(basePrice, discount);
  const calculatedDiscountedPrice = basePrice - calculatedDiscount;

  const bundleDiscountedPrice = isDynamic
    ? calculatedDiscountedPrice != 0
      ? Math.min(discountedPrice, calculatedDiscountedPrice)
      : discountedPrice
    : calculatedDiscountedPrice;

  const footerStepInfoWithProgressBar = (
    <div>
      <Span
        modifiers={
          bundleDiscountedPrice < basePrice ? 'strikethrough' : 'emphasis'
        }
      >
        {format(basePrice)}
      </Span>
      {bundleDiscountedPrice < basePrice && (
        <Span modifiers={'emphasis'}>{` ${format(
          bundleDiscountedPrice,
        )}`}</Span>
      )}
    </div>
  );

  useEffect(() => {
    const products = bundleData.productsInCollection?.length
      ? bundleData.productsInCollection
      : (bundleData.productsInSections as any).flatMap(
          ({ products, productsInCollections }) => [
            ...products.flat().map(({ productData = {} }) => productData),
            ...productsInCollections
              .flat()
              .map(({ productData = {} }) => productData),
          ],
        );
    const sortedByInitiallySelectedProducts = sortProductsBySelected(
      products,
      initialSelectedProducts,
    );
    setFilteredProducts(sortedByInitiallySelectedProducts);
  }, [
    bundleData.productsInCollection,
    bundleData.productsInSections,
    initialSelectedProducts,
  ]);

  const noTouched = isEqual(
    initialSelectedProducts,
    removeZeros(selectedProducts),
  );

  const {
    selectedProductsBySection,
    addProductToSection,
    removeProductFromSection,
    handleSetSelectedProductsBySection,
  } = useBundleSectionsSelection({
    bundleStep: settings.StepSize,
    addProduct: addProduct,
    removeProduct: removeProduct,
    setSelectedProducts: setSelectedProducts,
    productsInSections: bundleData.productsInSections,
    sections: bundle?.Sections as any[],
  });

  return (
    <>
      <CreateBundleContainer>
        {bundle.HasBuildABoxFiltersActive && (
          <FiltersSection
            setSelectedFilters={setSelectedFilters}
            selectedFilters={selectedFilters}
            handleResetFilters={handleResetFilters}
            filters={bundle.BuildABoxFilterGroups}
          />
        )}
        <main className="bundle-update-content bundle-scrollable-section">
          {hasBabSections ? (
            <CreateBundleWithSections
              bundle={bundle}
              productsInSections={bundleData.productsInSections}
              hideBaBSoldOutOptions={hideBaBSoldOutOptions}
              ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
              sellingPlan={sellingPlan}
              onCardClick={handleOnCardClick}
              canAddProducts={canAddProducts}
              addProduct={addProductToSection}
              removeProduct={removeProductFromSection}
              setSelectedProducts={handleSetSelectedProductsBySection}
              selectedProductsBySection={selectedProductsBySection}
              selectedProducts={selectedProducts}
              initialSelectedProducts={initialSelectedProducts}
              BaBProductsToCartFlag={isDynamic}
              size={size}
              filteredProducts={filteredProducts}
            />
          ) : (
            <ProductsHandler
              subscriptionLines={subscription.Lines}
              bundle={bundle}
              productsInCollection={filteredProducts}
              canAddProducts={canAddProducts}
              addProduct={addProduct}
              removeProduct={removeProduct}
              selectedProducts={selectedProducts}
              onCardClick={handleOnCardClick}
              hasSections={bundle.HasSections}
              discount={discount}
              initialSelectedProducts={initialSelectedProducts}
              setSelectedProducts={setSelectedProducts}
              shopConfiguration={shopConfiguration}
              sellingPlan={sellingPlan}
              hideBaBSoldOutOptions={hideBaBSoldOutOptions}
              ignoreBaBOutOfStockAllowance={ignoreBaBOutOfStockAllowance}
            />
          )}
        </main>
        {(!isDynamic || (isDynamic && !hasDynamicSize)) && (
          <ProgressBar
            total={size}
            selected={productsLength}
            selectedVariantsId={selectedProducts}
            isBabWithSectionsContent={hasBabSections}
            selectedProductsBySection={selectedProductsBySection}
            removeProductFromSection={removeProductFromSection}
            removeProduct={removeProduct}
            countLabel={`${productsLength} ${t('bundles.of')} ${size}`}
            productsInCollection={bundleData.productsInCollection}
          />
        )}
        <footer>
          <CreateBundleFooter>
            <BundleSelector
              isPrepaid={isPrepaid}
              hasDynamicSize={isDynamic && hasDynamicSize}
              settings={getAllowedBaBSwapVariants()}
              selectedSetting={selectedSetting as string}
              onUpdateSetting={handleOnUpdateSetting}
              originalSettings={bundle.Settings}
            />
            <CreateBundleButtonInfo>
              {footerStepInfoWithProgressBar}
              <span>
                {isDynamic && hasDynamicSize
                  ? t(`bundles.dynamicSize.selection`, {
                      count: productsLength,
                      item: t(
                        `bundles.dynamicSize.item.${
                          productsLength > 1 ? 'plural' : 'singular'
                        }`,
                      ),
                    })
                  : `${productsLength} ${t('bundles.of')} ${size}`}
              </span>
              <Button
                onClick={handleNext}
                filled
                disabled={!isSaveable || noTouched}
                aria-disabled={!isSaveable || noTouched}
              >
                {t('bundles.save')}
              </Button>
            </CreateBundleButtonInfo>
          </CreateBundleFooter>
        </footer>
      </CreateBundleContainer>
      {isOpen && (
        <SlidePanel
          id={productInfo.id}
          handle={productInfo.handle}
          open={isOpen}
          onClose={handleOnCloseSlidePanel}
          imgUrl={productInfo.image}
          imgs={productInfo.images}
          productName={productInfo.title}
          variantName={productInfo.variantTitle}
          subtitle={productInfo.subtitle}
          description={productInfo.description}
          hasOneVariant={productInfo?.hasOneVariant}
          isLoading={!productInfo || isEmpty(productInfo)}
          hidePrice={subscription.ContainsGift}
          reviews={productInfo.reviews}
        />
      )}
    </>
  );
};

const fetchIndividualProducts = async (
  products: string[],
  result: LiquidProductInCollection[] = [],
): Promise<LiquidProductInCollection[]> => {
  const chunkOfProducts = products.splice(0, MAX_CHUNK_SIZE);

  if (chunkOfProducts.length === 0) {
    return result;
  }

  const productsInCollection = await getBaBSingleProducts(
    chunkOfProducts.join(','),
  );

  return fetchIndividualProducts(products, [
    ...result,
    ...productsInCollection,
  ]);
};

const getIndividualProducts = async (
  productsInfo,
  isVariantCollapsed = false,
): Promise<GlobalVariant[] | GlobalProduct[]> => {
  let singleVariantsCollection: GlobalVariant[] | GlobalProduct[] = [];

  const individualProductsCollection = productsInfo
    ? await fetchIndividualProducts(productsInfo.map(({ Handle }) => Handle))
    : [];

  if (individualProductsCollection && productsInfo?.length) {
    const variants = flatMap(productsInfo, ({ Variants }) => {
      return Variants;
    });

    singleVariantsCollection = !isVariantCollapsed
      ? flatMap(individualProductsCollection, (variantData) =>
          extractVariantAndImage(variantData),
        ).filter((variant) => getOnlySelectedVariants(variant, variants))
      : individualProductsCollection
          .map(({ productData, productMetafields, variantsInventory }) =>
            formatLiquidProductToGlobalProduct(
              {
                ...productData,
                metafields: productMetafields,
              },
              productsInfo,
              variantsInventory,
            ),
          )
          .filter(notEmpty);
  }
  return singleVariantsCollection;
};

const formatBundleCollections = async (
  {
    productsInCollection,
    productsInAddonsCollection,
    productsInfo,
    productsInSections,
    ...bundle
  }: LiquidBundle,
  sellingPlan: string,
  variant: string,
  productsInBundle: Record<string, number>,
): Promise<{
  bundle: Bundle;
  productsInCollection: GlobalVariant[] | GlobalProduct[];
  productsInAddonsCollection: GlobalVariant[] | GlobalProduct[];
  productsInSections?: BabSection[];
}> => {
  const isVariantCollapsed =
    bundle.bundle.VariantStyle === VariantStyle.collapse;

  const sectionProductsInCollection = productsInSections?.flatMap(
    ({ productsInCollections }) => productsInCollections.flat(),
  );

  const hasSections = bundle?.bundle?.Sections?.length > 0;

  const sectionProductsInfo = productsInSections?.flatMap(({ products }) => {
    return products;
  });

  const productsCollection = hasSections
    ? sectionProductsInCollection
    : productsInCollection;

  const productsInformation = hasSections ? sectionProductsInfo : productsInfo;

  const singleVariantsCollection = productsInformation?.length
    ? await getIndividualProducts(productsInformation, isVariantCollapsed)
    : [];

  const variantsCollections: GlobalVariant[] | GlobalProduct[] =
    !isVariantCollapsed
      ? (flatMap(productsCollection, (productData) =>
          extractVariantAndImage(productData),
        ) as GlobalVariant[])
      : productsCollection
          .map(({ productData, productMetafields, variantsInventory }) =>
            formatLiquidProductToGlobalProduct(
              {
                ...productData,
                metafields: productMetafields,
              },
              productsInformation,
              variantsInventory,
            ),
          )
          .filter(notEmpty);

  const addonsCollections = !isVariantCollapsed
    ? flatMap(productsInAddonsCollection, (addonData) =>
        extractVariantAndImage(addonData, true),
      )
    : productsInAddonsCollection
        .map(({ productData, productMetafields, variantsInventory }) =>
          formatLiquidProductToGlobalProduct(
            {
              ...productData,
              metafields: productMetafields,
            },
            [],
            variantsInventory,
          ),
        )
        .filter(notEmpty);

  let collection =
    singleVariantsCollection.length > 0
      ? singleVariantsCollection
      : variantsCollections;

  if (isGlobalProductList(collection)) {
    collection = splitProductInVariants(collection, productsInBundle);
  }

  const bundleFormat = {
    ...bundle.bundle,
    SellingPlan: sellingPlan,
    VariantId: variant,
  };

  return {
    ...bundle,
    productsInCollection: collection,
    productsInAddonsCollection: addonsCollections,
    productsInSections: productsInSections,
    bundle: bundleFormat,
  };
};

interface Attribute {
  key: string;
  value: string;
}

const convertPropertiesToAttributes = (
  itemProperties: BundleCartProperties,
): Attribute[] => {
  const newCustomProperties = Object.entries(itemProperties).reduce(
    (acc, [key, value]) => {
      const attribute = {
        key,
        value: value.toString(),
      };
      return [...acc, attribute];
    },
    [] as Attribute[],
  );

  return newCustomProperties;
};

const sortProductsBySelected = (products, selected) => {
  const sortedProducts = products.reduce((acc: GlobalVariant[], element) => {
    if (Object.keys(selected).includes(element.id.toString())) {
      return [element, ...acc];
    }
    return [...acc, element];
  }, []);
  return sortedProducts;
};

function removeZeros(obj2: Record<string, number>) {
  return Object.entries(obj2).reduce(
    (acc, [key, value]: [string, number]) => {
      if (value === null || value === undefined || value === 0) {
        return acc;
      }
      return { ...acc, [key]: value };
    },
    {} as Record<string, number>,
  );
}
